import { vibrate, VIBRATION_ERKANNT } from '../lib/haptics'
import { STRINGS } from '../lib/i18n'
import { useEffect, useRef, useState } from 'react'
import { useChatStore } from '../state/chatStore'
import { debugGewuenscht, voiceDiag } from '../lib/voiceDiagnostics'
import { analyzeImage, frameFrom, loadShowcaseTitles } from '../lib/imageAnalysis'
import { VoiceDiagnostics } from './VoiceDiagnostics'

interface RecognitionResult {
  showcaseId: string | null
  confidence: number
  alternatives: { showcaseId: string; confidence: number }[]
  /** Wortlaut des Modells, nur für die Debug-Anzeige. */
  debug?: string
  /** Kennung des protokollierten Ergebnisses — für das Ja/Nein des Besuchers (kein Bild). */
  eventId?: string | null
}

const CAPTURE_INTERVAL_MS = 1500
// Mirrors AppConfig.showcaseRecognitionStableFrames' default (packages/shared) —
// keep in sync if that default changes.
const STABLE_FRAMES_REQUIRED = 3
/**
 * Sieht das kleine Modell so viele Bilder in Folge nichts, fragt die Schleife
 * das schnelle Sprachmodell — höchstens alle `LLM_MINDESTABSTAND_MS`. Das
 * kleine Modell sagte vor dem echten Spiegel oft „keiner", das große sah ihn
 * im selben Bild; die Schleife lief trotzdem nur mit dem kleinen. Ein Treffer
 * des Sprachmodells zählt sofort als stabil — es rät nicht zwischen Bildern.
 */
const LEER_BIS_LLM = 2
const LLM_MINDESTABSTAND_MS = 8000
/** Erster Blick beim Öffnen: so lange darf er dauern, dann ist der Knopf frei. */
const ERSTER_BLICK_MS = 5000
/** Die Kamera braucht einen Moment für Belichtung und Fokus — ein Bild davor ist Matsch. */
const BELICHTUNG_MS = 700
/**
 * So viele Fehler in Folge (Ratenlimit, 5xx, Netz) hält die Schleife an.
 * Beim Opening mit vielen offenen Kameras kann das Bildmodell an seine Grenze
 * kommen; dann soll der Besucher nicht vor einem Bild stehen, das scheinbar
 * nichts erkennt, sondern sagen, wo er ist — ein Tippen, und K4ia erklärt.
 */
const FEHLER_BIS_PAUSE = 3

/**
 * FR-30..FR-34: camera mode. Recognizes the showcase in front of the visitor
 * (stable over several frames before announcing, FR-31/FR-33) and supports
 * free-form image questions ("Was ist das?", FR-32). Never stores a frame —
 * captures live to a canvas, sends it, discards it (FR-34).
 */
/**
 * Das Ja/Nein des Besuchers zur Erkennung — die einzige Rückmeldung, die die
 * Kamera bekommt, ob sie richtig lag. Sie geht als Ergebnis ins Protokoll
 * (Admin „Erkennung"), ohne Bild. Ein Fehler hier ist egal für das Gespräch.
 */
function bestaetigeErkennung(eventId: string | null, confirmed: boolean): void {
  if (!eventId) return
  void fetch(`/api/v1/vision/events/${encodeURIComponent(eventId)}/confirm`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ confirmed }),
  }).catch(() => undefined)
}

export function CameraView({ onClose }: { onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  // Der Effekt läuft einmal; die aktuelle onClose-Funktion holt er sich hier.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const [recognized, setRecognized] = useState<{
    id: string
    title: string
    eventId: string | null
  } | null>(null)
  /** Im Sprachmodus hat K4ia den Showcase schon angesagt — kein Ja/Nein nötig. */
  const [angesagt, setAngesagt] = useState<string | null>(null)
  const [ambiguous, setAmbiguous] = useState<string[] | null>(null)
  // Als Ref, nicht als State: Die Erkennungsschleife läuft in einem Effekt,
  // der einmal beim Einhängen startet — ein State darin bliebe für immer auf
  // seinem Anfangswert stehen, und „Nein danke" hätte nach drei Bildern wieder
  // denselben Hinweis gebracht. Im Sprachmodus hieße das: K4ia sagt denselben
  // Showcase alle fünf Sekunden neu an.
  const dismissedRef = useRef<string | null>(null)
  const [question, setQuestion] = useState('')
  /** „Was siehst du gerade?" läuft — Erkennung, dann Antwort. */
  const [schautHin, setSchautHin] = useState(false)
  /**
   * Der erste Blick beim Öffnen: K4ia schaut sofort hin, ohne dass jemand
   * drückt. Solange läuft, ist der Knopf ausgegraut und sagt, was passiert.
   * Nach `ERSTER_BLICK_MS` ohne Treffer wird der Knopf frei — und schaut beim
   * Drücken mit dem großen Modell genauer hin.
   */
  const [ersterBlick, setErsterBlick] = useState(true)
  /** Der Knopf hat nichts erkannt — kurz an Ort und Stelle sagen, nicht im Chat. */
  const [nichtsErkannt, setNichtsErkannt] = useState(false)
  const lastGuessRef = useRef<{ id: string; count: number } | null>(null)
  /** Bilder in Folge, auf denen das kleine Modell nichts sah. */
  const leerInFolgeRef = useRef(0)
  const letzteLlmAnfrageRef = useRef(0)
  /** Während der Knopf arbeitet, pausiert die Schleife — sonst reden zwei Erkennungen durcheinander. */
  const knopfAktivRef = useRef(false)
  const showcaseTitles = useRef(new Map<string, string>())
  /** Fehler in Folge in der Schleife; ab `FEHLER_BIS_PAUSE` zeigt die Kamera die Stände zur Wahl. */
  const fehlerInFolgeRef = useRef(0)
  /** Ein Bild ist gerade beim Worker — bis zur Antwort schickt die Schleife kein zweites. */
  const bildUnterwegsRef = useRef(false)
  const [ueberlastet, setUeberlastet] = useState<{ id: string; title: string }[] | null>(null)

  const sendMessage = useChatStore((s) => s.sendMessage)
  const addUserTranscript = useChatStore((s) => s.addUserTranscript)

  useEffect(() => {
    let stream: MediaStream | null = null
    let intervalId: ReturnType<typeof setInterval> | null = null
    let cancelled = false

    async function setup() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        if (videoRef.current) videoRef.current.srcObject = stream

        // Titel der Live-Showcases, einmal je Seite (öffentlicher Endpunkt).
        showcaseTitles.current = await loadShowcaseTitles()

        intervalId = setInterval(() => void captureAndRecognize(), CAPTURE_INTERVAL_MS)
        void ersterBlickStarten()
      } catch {
        // permission denied or no camera — silently stay in text mode, ChatView doesn't force camera use
      }
    }

    /**
     * Sobald das Bild scharf ist (Belichtung braucht einen Moment), einmal mit
     * dem schnellen Sprachmodell hinschauen. Erkannt → Kamera zu, wie beim
     * Knopf. Nicht erkannt oder Zeit vorbei → Knopf frei, Schleife läuft weiter.
     */
    async function ersterBlickStarten() {
      const start = Date.now()
      while (!cancelled && (!videoRef.current || videoRef.current.videoWidth === 0)) {
        if (Date.now() - start > 3000) break
        await new Promise((r) => setTimeout(r, 100))
      }
      if (cancelled || !videoRef.current || videoRef.current.videoWidth === 0) {
        setErsterBlick(false)
        return
      }
      await new Promise((r) => setTimeout(r, BELICHTUNG_MS))
      if (cancelled) return
      knopfAktivRef.current = true
      try {
        const video = videoRef.current
        const ergebnis = await analyzeImage(
          { klein: frameFrom(video, 512), gross: frameFrom(video, 768, 0.85) },
          { modus: 'kamera', stage2: 'fast', timeoutMs: ERSTER_BLICK_MS },
        )
        if (cancelled) return
        if (ergebnis.art === 'showcase') {
          dismissedRef.current = ergebnis.id
          vibrate(VIBRATION_ERKANNT)
          onCloseRef.current()
          return
        }
        if (ergebnis.art === 'mehrere') {
          vibrate(VIBRATION_ERKANNT)
          onCloseRef.current()
          return
        }
      } catch {
        // Zeit vorbei oder Netz weg: dann eben der Knopf.
      } finally {
        knopfAktivRef.current = false
        if (!cancelled) setErsterBlick(false)
      }
    }

    async function captureAndRecognize() {
      if (knopfAktivRef.current) return
      // Ein Bild nach dem anderen: Der Takt läuft weiter, auch wenn die
      // Antwort länger braucht als anderthalb Sekunden — im Lasttest (30
      // Kameras) lag das kleine Modell bei 1,8 s, und ohne diese Sperre
      // hätte jede Kamera zwei Bilder zugleich in der Leitung gehabt.
      if (bildUnterwegsRef.current) return
      const video = videoRef.current
      if (!video || video.videoWidth === 0) return
      bildUnterwegsRef.current = true
      try {
        await erkenneBild(video)
      } finally {
        bildUnterwegsRef.current = false
      }
    }

    async function erkenneBild(video: HTMLVideoElement) {
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = (512 * video.videoHeight) / video.videoWidth
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8)

      voiceDiag.kameraBilder++
      const jetzt = Date.now()
      const mitLlm =
        leerInFolgeRef.current >= LEER_BIS_LLM &&
        jetzt - letzteLlmAnfrageRef.current >= LLM_MINDESTABSTAND_MS
      if (mitLlm) letzteLlmAnfrageRef.current = jetzt
      try {
        const res = await fetch('/api/v1/vision/recognize', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(
            mitLlm
              ? { imageDataUrl, thorough: true, stage2: 'fast', source: 'loop' }
              : { imageDataUrl, source: 'loop' },
          ),
        })
        if (!res.ok) {
          // Vorher stillschweigend übergangen — und ein Endpunkt, der 400 oder
          // 502 antwortet, sah damit genauso aus wie einer, der nichts erkennt.
          const text = await res.text().catch(() => '')
          voiceDiag.kameraLetzte = `HTTP ${res.status} ${text.slice(0, 80)}`
          if (res.status === 429 || res.status >= 500) zaehleFehler()
          return
        }
        fehlerInFolgeRef.current = 0
        const result = (await res.json()) as RecognitionResult
        voiceDiag.kameraAntworten++
        voiceDiag.kameraLetzte = `${
          result.showcaseId
            ? (showcaseTitles.current.get(result.showcaseId) ?? result.showcaseId)
            : 'nichts'
        } (${result.confidence.toFixed(2)})${mitLlm ? ' [LLM]' : ''}${result.debug ? ` — Modell: „${result.debug}"` : ''}`
        handleResult(result, mitLlm)
      } catch (err) {
        voiceDiag.kameraLetzte = `Netzfehler: ${err instanceof Error ? err.message : String(err)}`
        zaehleFehler()
      }
    }

    /** Überlastet: Schleife anhalten und die Stände zur Wahl stellen. */
    function zaehleFehler() {
      fehlerInFolgeRef.current += 1
      if (fehlerInFolgeRef.current < FEHLER_BIS_PAUSE || cancelled) return
      if (intervalId) clearInterval(intervalId)
      intervalId = null
      setUeberlastet([...showcaseTitles.current].map(([id, title]) => ({ id, title })))
    }

    function handleResult(result: RecognitionResult, vomLlm = false) {
      if (result.showcaseId) {
        leerInFolgeRef.current = 0
        if (lastGuessRef.current?.id === result.showcaseId) {
          lastGuessRef.current.count += 1
        } else {
          lastGuessRef.current = { id: result.showcaseId, count: 1 }
        }
        // Das Sprachmodell rät nicht zwischen Bildern — sein Treffer gilt sofort.
        if (vomLlm) lastGuessRef.current.count = STABLE_FRAMES_REQUIRED
        voiceDiag.kameraStabil = lastGuessRef.current.count
        if (
          lastGuessRef.current.count >= STABLE_FRAMES_REQUIRED &&
          result.showcaseId !== dismissedRef.current
        ) {
          const title = showcaseTitles.current.get(result.showcaseId) ?? 'diesen Showcase'
          vibrate(VIBRATION_ERKANNT)
          // Läuft ein Sprachgespräch, sagt K4ia von sich aus, wo der Besucher
          // steht — er hält gerade keinen Knopf und wartet vor dem Stand. Der
          // Showcase gilt danach als erledigt, bis ein anderer erkannt wird.
          const sender = useChatStore.getState().voiceMarkerSender
          if (sender) {
            sender(`[[kamera:${result.showcaseId}]]`)
            addUserTranscript(
              `${STRINGS[useChatStore.getState().locale].tourArrived} ${title}`,
              false,
            )
            dismissedRef.current = result.showcaseId
            setAngesagt(title)
            setRecognized(null)
          } else {
            setRecognized({ id: result.showcaseId, title, eventId: result.eventId ?? null })
          }
          setAmbiguous(null)
        }
      } else {
        lastGuessRef.current = null
        // Unsicher (eine Nummer, aber unter der Schwelle) zählt wie zwei leere
        // Bilder: Dann lohnt der Blick des Sprachmodells mit den Referenzfotos
        // gleich, nicht erst nach weiteren leeren Bildern.
        leerInFolgeRef.current += result.confidence > 0 ? LEER_BIS_LLM : 1
        voiceDiag.kameraStabil = 0
        if (result.alternatives.length >= 2) {
          setAmbiguous(
            result.alternatives.map(
              (a) => showcaseTitles.current.get(a.showcaseId) ?? a.showcaseId,
            ),
          )
        }
      }
    }

    void setup()
    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
      stream?.getTracks().forEach((t) => t.stop())
    }
    // Intentionally runs once per mount — re-running on every state change
    // would tear down and restart the camera stream and capture loop.
  }, [])

  function explainRecognized() {
    if (!recognized) return
    void sendMessage(
      `${STRINGS[useChatStore.getState().locale].tourArrived} ${recognized.title}`,
      recognized.id,
    )
    dismissedRef.current = recognized.id
    bestaetigeErkennung(recognized.eventId, true)
    setRecognized(null)
  }

  function dismissRecognized() {
    if (recognized) {
      dismissedRef.current = recognized.id
      bestaetigeErkennung(recognized.eventId, false)
    }
    setRecognized(null)
  }

  /** Überlastete Kamera: Der Besucher sagt selbst, wo er steht. */
  function standGewaehlt(stand: { id: string; title: string }) {
    dismissedRef.current = stand.id
    void sendMessage(
      `${STRINGS[useChatStore.getState().locale].tourArrived} ${stand.title}`,
      stand.id,
    )
    onClose()
  }

  function askAboutFrame(e: React.FormEvent) {
    e.preventDefault()
    if (!videoRef.current || !question.trim()) return
    void sendMessage(question, undefined, frameFrom(videoRef.current, 768, 0.85))
    setQuestion('')
  }

  /**
   * Der eine Knopf für „Was siehst du gerade?": aktuelles Bild, Erkennung,
   * Antwort — im Chat, und im Sprachmodus gesprochen. Ohne Tippen.
   */
  async function whatDoYouSee() {
    if (!videoRef.current || videoRef.current.videoWidth === 0 || schautHin) return
    setSchautHin(true)
    knopfAktivRef.current = true
    try {
      const video = videoRef.current
      const ergebnis = await analyzeImage(
        { klein: frameFrom(video, 512), gross: frameFrom(video, 768, 0.85) },
        { modus: 'kamera', stage2: 'strong' },
      )
      if (ergebnis.art === 'showcase') {
        // Erkannt — die Kamera hat ihren Zweck erfüllt. Sie schließt sich, damit
        // die Erklärung den Platz bekommt; wer weiterschauen will, öffnet sie
        // wieder.
        dismissedRef.current = ergebnis.id
        onClose()
      } else if (ergebnis.art === 'mehrere') {
        // Die ganze Vitrine: K4ia fragt im Chat nach, welcher es sein soll —
        // dafür braucht sie den Platz, nicht das Kamerabild.
        onClose()
      } else {
        setNichtsErkannt(true)
        setTimeout(() => setNichtsErkannt(false), 4000)
      }
    } finally {
      knopfAktivRef.current = false
      setSchautHin(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 px-4 pt-3">
      {/* Bewusst klein: knapp ein Drittel der Höhe, Bild beschnitten statt
          skaliert. Vorher füllte die Vorschau den ganzen Bildschirm, und wer
          „Ja, gerne" tippte, musste die Kamera ausschalten, um die Antwort zu
          lesen. Die Erkennung braucht kein großes Bild — sie bekommt ohnehin
          nur 512 px. */}
      <div className="relative h-[28vh] max-h-56 overflow-hidden rounded-xl bg-black">
        <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />

        {recognized && (
          <div className="absolute inset-x-2 bottom-2 rounded-lg bg-bg-elevated/95 p-3 text-sm shadow-lg">
            <p className="mb-2">
              Du stehst vor dem <strong>{recognized.title}</strong>-Showcase. Soll ich dir mehr
              erzählen?
            </p>
            <div className="flex gap-2">
              <button onClick={explainRecognized} className="btn-primary rounded-md px-3 py-1.5">
                Ja, gerne
              </button>
              <button
                onClick={dismissRecognized}
                className="rounded-md border border-border px-3 py-1.5"
              >
                Nein danke
              </button>
            </div>
          </div>
        )}

        {schautHin && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/40 text-sm font-medium text-white"
            aria-live="polite"
          >
            K4ia schaut hin …
          </div>
        )}

        {!schautHin && nichtsErkannt && (
          <div className="absolute inset-x-2 bottom-2 rounded-lg bg-bg-elevated/95 p-3 text-sm shadow-lg">
            Hier erkenne ich keinen Showcase. Geh etwas näher ran oder ändere den Winkel.
          </div>
        )}

        {!recognized && angesagt && (
          <div className="absolute inset-x-2 bottom-2 rounded-lg bg-bg-elevated/95 p-3 text-sm shadow-lg">
            <strong>{angesagt}</strong> erkannt — K4ia erzählt dir davon.
          </div>
        )}

        {!recognized && !angesagt && ambiguous && !ueberlastet && (
          <div className="absolute inset-x-2 bottom-2 rounded-lg bg-bg-elevated/95 p-3 text-sm shadow-lg">
            Meinst du {ambiguous.join(' oder ')}?
          </div>
        )}

        {ueberlastet && !recognized && (
          <div
            className="absolute inset-x-2 bottom-2 rounded-lg bg-bg-elevated/95 p-3 text-sm shadow-lg"
            role="status"
            data-testid="kamera-ueberlastet"
          >
            <p className="mb-2">
              {ueberlastet.length > 0
                ? STRINGS[useChatStore.getState().locale].cameraBusy
                : STRINGS[useChatStore.getState().locale].cameraBusyNone}
            </p>
            {ueberlastet.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {ueberlastet.map((stand) => (
                  <button
                    key={stand.id}
                    type="button"
                    onClick={() => standGewaehlt(stand)}
                    className="rounded-full border border-border bg-bg px-3 py-1.5 text-sm"
                  >
                    {stand.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => void whatDoYouSee()}
        disabled={schautHin || ersterBlick}
        className="btn-primary w-full rounded-xl px-4 py-3 font-semibold disabled:opacity-60"
      >
        {ersterBlick
          ? 'K4ia versucht, den Showcase zu erkennen …'
          : schautHin
            ? 'K4ia schaut genauer hin …'
            : 'Was siehst du gerade?'}
      </button>

      <form onSubmit={askAboutFrame} className="flex gap-2">
        <label htmlFor="camera-question" className="sr-only">
          Frage zum aktuellen Kamerabild
        </label>
        <input
          id="camera-question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Eigene Frage zum Bild …"
          className="min-w-0 flex-1 rounded-xl border border-border bg-bg-elevated px-4 py-2 outline-none focus:border-brand"
        />
        <button
          type="submit"
          disabled={!question.trim()}
          className="btn-primary rounded-xl px-4 py-2"
        >
          Fragen
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-border px-4 py-2 text-sm"
        >
          Kamera aus
        </button>
      </form>

      {debugGewuenscht() && <VoiceDiagnostics status="kamera" />}
    </div>
  )
}
